import React, { useState } from "react";

const BRAND_RED = "#E31E24";
const BRAND_NAVY = "#2B2B6B";

// Course "type" legend — adjust the wording if your academic department
// uses different labels for C / T / H.
const TYPE_LABELS = {
  C: "Core",
  T: "Technical / Practical",
  H: "Humanities / Elective",
};

// ── Program data, built from the 2026 curriculum spreadsheets ─────────
const programs = [
  {
    id: "liberal-arts",
    shortName: "Liberal Arts",
    degreeTitle: "Associate Degree in Liberal Arts",
    tagline:
      "A broad, humanities-driven foundation spanning communication, research, social science, and the arts — built for students who want range before they specialize.",
    modality: "In-person",
    term: "Semester 01-2026 – Semester 02-2027",
    length: "4 semesters",
    totalCredits: 70,
    semesters: [
      {
        number: 1,
        courses: [
          { num: 1, code: "RMT210", name: "Research Methods", prereq: "High School", credits: 4, type: "C" },
          { num: 2, code: "MAT110", name: "Algebra", prereq: "High School", credits: 4, type: "C" },
          { num: 3, code: "COM103", name: "Communications", prereq: "High School", credits: 3, type: "T" },
          { num: 4, code: "ANTHS1600", name: "Social Anthropology", prereq: "High School", credits: 3, type: "C" },
          { num: 5, code: "ECO301", name: "Introduction to Economics", prereq: "High School", credits: 4, type: "H" },
        ],
      },
      {
        number: 2,
        courses: [
          { num: 6, code: "IT110", name: "Information Technology", prereq: "High School", credits: 3, type: "C" },
          { num: 7, code: "MAT135", name: "Calculus I", prereq: "Algebra", credits: 4, type: "C" },
          { num: 8, code: "PSCY101", name: "General Psychology", prereq: "High School", credits: 3, type: "C" },
          { num: 9, code: "ENGLISH1015", name: "Academic Writing", prereq: "High School", credits: 3, type: "C" },
          { num: 10, code: null, name: "Elective 1", prereq: "—", credits: 3, type: "H", elective: true },
        ],
      },
      {
        number: 3,
        courses: [
          { num: 11, code: "PHIL318", name: "Ethics", prereq: "High School", credits: 3, type: "C" },
          { num: 12, code: "MAT117", name: "Statistics", prereq: "High School", credits: 4, type: "T" },
          { num: 13, code: "CHE101", name: "General Chemistry", prereq: "High School", credits: 4, type: "C" },
          { num: 14, code: "ENG210", name: "English Literature", prereq: "High School", credits: 3, type: "C" },
          { num: 15, code: null, name: "Elective 2", prereq: "—", credits: 3, type: "H", elective: true },
        ],
      },
      {
        number: 4,
        courses: [
          { num: 16, code: "POL101", name: "Political Science", prereq: "High School", credits: 3, type: "H" },
          { num: 17, code: "ECON1008", name: "Data Analytics", prereq: "Information Technology", credits: 3, type: "T" },
          { num: 18, code: "PREM295", name: "Project Management", prereq: "Research Methods", credits: 4, type: "C" },
          { num: 19, code: null, name: "Integrated Project", prereq: "15 completed courses", credits: 6, type: "T" },
          { num: 20, code: null, name: "Elective 3", prereq: "—", credits: 3, type: "H", elective: true },
        ],
      },
    ],
    electives: [
      { code: "ENSC", name: "Environmental Studies", prereq: "High School", credits: 3, type: "H" },
      { code: "HIST103", name: "United States History", prereq: "High School", credits: 3, type: "H" },
      { code: "ICSS139", name: "Geography and Tourism", prereq: "High School", credits: 3, type: "C" },
      { code: "PHY111", name: "General Physics", prereq: "High School", credits: 3, type: "H" },
      { code: "BIO110", name: "General Biology", prereq: "High School", credits: 3, type: "H" },
      { code: "MICROB", name: "Microbiology", prereq: "General Biology", credits: 3, type: "H" },
      { code: "ESTUCUL", name: "Cultural Studies", prereq: "High School", credits: 3, type: "C" },
      { code: "SIS105", name: "International Relations", prereq: "High School", credits: 3, type: "C" },
      { code: "SPA240", name: "Spanish Grammar", prereq: "High School", credits: 3, type: "H" },
    ],
  },
  {
    id: "business-administration",
    shortName: "Business Administration",
    degreeTitle: "Associate Degree in Business Administration",
    tagline:
      "A practice-oriented path through accounting, finance, marketing, and management — built for students heading straight into the business world.",
    modality: "In-person",
    term: "Semester 01-2026 – Semester 01-2028",
    length: "5 semesters",
    totalCredits: 92,
    semesters: [
      {
        number: 1,
        courses: [
          { num: 1, code: "ACC101", name: "Introduction to Financial Accounting", prereq: "High School", credits: 4, type: "C" },
          { num: 2, code: "MAT110", name: "Algebra", prereq: "High School", credits: 4, type: "C" },
          { num: 3, code: "RMT210", name: "Research Methods", prereq: "High School", credits: 4, type: "T" },
          { num: 4, code: "BCOMM", name: "Business Communication", prereq: "High School", credits: 3, type: "C" },
          { num: 5, code: "ECO301", name: "Introduction to Economics", prereq: "High School", credits: 4, type: "H" },
        ],
      },
      {
        number: 2,
        courses: [
          { num: 6, code: "AC102", name: "Financial Accounting", prereq: "Introduction to Financial Accounting", credits: 4, type: "C" },
          { num: 7, code: "MAT135", name: "Calculus I", prereq: "Algebra", credits: 4, type: "C" },
          { num: 8, code: "BUS120", name: "Business Management", prereq: "High School", credits: 3, type: "C" },
          { num: 9, code: "ECO201", name: "Microeconomics", prereq: "Introduction to Economics", credits: 4, type: "C" },
          { num: 10, code: null, name: "Elective 1", prereq: "—", credits: 3, type: "H", elective: true },
        ],
      },
      {
        number: 3,
        courses: [
          { num: 11, code: "ACC185", name: "Managerial Accounting", prereq: "Financial Accounting", credits: 4, type: "C" },
          { num: 12, code: "MAT117", name: "General Statistics", prereq: "High School", credits: 4, type: "T" },
          { num: 13, code: "MKT200", name: "Marketing", prereq: "High School", credits: 4, type: "C" },
          { num: 14, code: "ECO202", name: "Macroeconomics", prereq: "Microeconomics", credits: 4, type: "C" },
          { num: 15, code: null, name: "Elective 2", prereq: "—", credits: 3, type: "H", elective: true },
        ],
      },
      {
        number: 4,
        courses: [
          { num: 16, code: "FIN621", name: "Financial Management", prereq: "Financial Accounting", credits: 4, type: "H" },
          { num: 17, code: "BUS227", name: "Business Ethics", prereq: "High School", credits: 3, type: "T" },
          { num: 18, code: "IT110", name: "Information Technology", prereq: "High School", credits: 3, type: "C" },
          { num: 19, code: "BUS606", name: "Operations & Supply Chain Management", prereq: "Business Management", credits: 3, type: "T" },
          { num: 20, code: null, name: "Elective 3", prereq: "—", credits: 3, type: "H", elective: true },
        ],
      },
      {
        number: 5,
        courses: [
          { num: 21, code: "PRM295", name: "Project Management", prereq: "Research Methods", credits: 4, type: "C" },
          { num: 22, code: "BUS244", name: "International Commerce", prereq: "Macroeconomics", credits: 4, type: "C" },
          { num: 23, code: "BUS275", name: "Human Talent Management", prereq: "High School", credits: 3, type: "C" },
          { num: 24, code: null, name: "Integrated Project", prereq: "20 completed courses", credits: 6, type: "T" },
          { num: 25, code: null, name: "Elective 4", prereq: "—", credits: 3, type: "H", elective: true },
        ],
      },
    ],
    electives: [
      { code: "MOS2181", name: "Organizational Behaviour", prereq: "High School", credits: 3, type: "H" },
      { code: "PHL110", name: "Critical Thinking", prereq: "High School", credits: 3, type: "H" },
      { code: "BUS290", name: "Business Law", prereq: "High School", credits: 3, type: "C" },
      { code: "BUS253", name: "Business Leadership", prereq: "High School", credits: 3, type: "H" },
      { code: "MKTG3553", name: "Consumer Behaviour", prereq: "Marketing", credits: 3, type: "H" },
      { code: "BUAD497", name: "Strategic Management", prereq: "Business Management", credits: 3, type: "H" },
      { code: "SPA240", name: "Spanish Grammar", prereq: "High School", credits: 3, type: "C" },
      { code: "ECON1008", name: "Data Analytics", prereq: "Information Technology", credits: 3, type: "C" },
      { code: "MARK7365", name: "Digital Marketing", prereq: "Marketing", credits: 3, type: "H" },
    ],
  },
];

function TypeTag({ type }) {
  const palette = {
    C: { bg: `${BRAND_NAVY}12`, fg: BRAND_NAVY },
    T: { bg: `${BRAND_RED}12`, fg: BRAND_RED },
    H: { bg: "#00000010", fg: "#555" },
  };
  const c = palette[type] || palette.H;
  return (
    <span
      title={TYPE_LABELS[type]}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 22,
        height: 22,
        borderRadius: 6,
        background: c.bg,
        color: c.fg,
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {type}
    </span>
  );
}

function CourseRow({ course, isLast }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "28px 1fr auto auto",
        alignItems: "center",
        gap: "0.85rem",
        padding: "0.75rem 0",
        borderBottom: isLast ? "none" : "1px solid #eee",
      }}
    >
      <span
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 13,
          fontWeight: 700,
          color: course.elective ? "#bbb" : BRAND_RED,
        }}
      >
        {String(course.num).padStart(2, "0")}
      </span>

      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 14.5,
            fontWeight: 500,
            color: "#222",
            fontStyle: course.elective ? "italic" : "normal",
          }}
        >
          {course.name}
        </div>
        <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
          {course.code ? `${course.code} · ` : ""}Prerequisite: {course.prereq}
        </div>
      </div>

      <span
        className="outreach-show-mobile"
        style={{
          fontSize: 12,
          color: "#6b6b6b",
          justifySelf: "end",
          whiteSpace: "nowrap",
        }}
      >
        {course.credits} cr.
      </span>

      <TypeTag type={course.type} />
    </div>
  );
}

function SemesterAccordion({ semester, isOpen, onToggle }) {
  const totalCredits = semester.courses.reduce((s, c) => s + c.credits, 0);

  return (
    <div
      style={{
        background: "#ffffff",
        border: "0.5px solid #e0e0e0",
        borderRadius: 14,
        overflow: "hidden",
        marginBottom: "0.9rem",
        boxShadow: isOpen ? "0 8px 24px rgba(43,43,107,0.08)" : "0 2px 8px rgba(0,0,0,0.03)",
        transition: "box-shadow 0.25s ease",
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "1.1rem 1.4rem",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          font: "inherit",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: "0.75rem" }}>
          <span
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 20,
              fontWeight: 700,
              color: BRAND_NAVY,
            }}
          >
            Semester {semester.number}
          </span>
          <span style={{ fontSize: 12.5, color: "#999" }}>
            {semester.courses.length} courses · {totalCredits} credits
          </span>
        </div>

        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: isOpen ? BRAND_RED : "#f2f2f5",
            color: isOpen ? "#fff" : BRAND_NAVY,
            fontSize: 16,
            fontWeight: 600,
            transition: "transform 0.25s ease, background 0.25s ease, color 0.25s ease",
            transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
            flexShrink: 0,
          }}
        >
          +
        </span>
      </button>

      <div
        style={{
          maxHeight: isOpen ? 600 : 0,
          overflow: "hidden",
          transition: "max-height 0.35s ease",
        }}
      >
        <div style={{ padding: "0 1.4rem 1.2rem" }}>
          {semester.courses.map((c, i) => (
            <CourseRow key={c.num} course={c} isLast={i === semester.courses.length - 1} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ElectivesGrid({ electives }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
        gap: "0.85rem",
      }}
    >
      {electives.map((e) => (
        <div
          key={e.code}
          style={{
            background: "#ffffff",
            border: "0.5px solid #e0e0e0",
            borderRadius: 12,
            padding: "1rem 1.1rem",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 500, color: "#222", lineHeight: 1.3 }}>
              {e.name}
            </span>
            <TypeTag type={e.type} />
          </div>
          <span style={{ fontSize: 11.5, color: "#999" }}>
            {e.code} · {e.credits} cr. · Prereq: {e.prereq}
          </span>
        </div>
      ))}
    </div>
  );
}

function ProgramToggle({ active, onChange }) {
  return (
    <div
      style={{
        display: "inline-flex",
        background: "#f2f2f5",
        borderRadius: 100,
        padding: 4,
        gap: 4,
      }}
    >
      {programs.map((p) => {
        const isActive = p.id === active;
        return (
          <button
            key={p.id}
            onClick={() => onChange(p.id)}
            style={{
              border: "none",
              cursor: "pointer",
              padding: "0.6rem 1.3rem",
              borderRadius: 100,
              fontSize: 13.5,
              fontWeight: 500,
              fontFamily: "'DM Sans', sans-serif",
              background: isActive ? BRAND_NAVY : "transparent",
              color: isActive ? "#fff" : "#555",
              transition: "background 0.25s ease, color 0.25s ease",
              whiteSpace: "nowrap",
            }}
          >
            {p.shortName}
          </button>
        );
      })}
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "#999", marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 500, color: BRAND_NAVY }}>{value}</div>
    </div>
  );
}

export default function ProgramsSection() {
  const [activeId, setActiveId] = useState(programs[0].id);
  const [openSemester, setOpenSemester] = useState(1);

  const program = programs.find((p) => p.id === activeId);

  const handleSwitch = (id) => {
    setActiveId(id);
    setOpenSemester(1);
  };

  return (
    <section
      style={{
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
        padding: "4rem 2rem 5rem",
        maxWidth: 1100,
        margin: "0 auto",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=DM+Sans:wght@400;500&display=swap');

        @media (max-width: 640px) {
          .outreach-show-mobile { display: none !important; }
        }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: "2.75rem" }}>
        <span
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            color: BRAND_RED,
            marginBottom: 8,
          }}
        >
          Academic Programs
        </span>
        <h2
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 36,
            fontWeight: 700,
            color: BRAND_NAVY,
            margin: "0 0 12px",
            lineHeight: 1.15,
          }}
        >
          Two paths, one standard
          <br />
          of preparation.
        </h2>
        <p style={{ fontSize: 15, color: "#6b6b6b", maxWidth: 560, lineHeight: 1.6, margin: 0 }}>
          Explore our Associate Degree programs in Liberal Arts and Business
          Administration — each built semester by semester to take students
          from foundational courses into applied, career-ready work.
        </p>
      </div>

      {/* Program toggle */}
      <div style={{ marginBottom: "2rem" }}>
        <ProgramToggle active={activeId} onChange={handleSwitch} />
      </div>

      {/* Program overview card */}
      <div
        style={{
          background: "#ffffff",
          border: "0.5px solid #e0e0e0",
          borderRadius: 16,
          padding: "1.75rem 2rem",
          marginBottom: "2.5rem",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        }}
      >
        <div
          style={{
            width: 32,
            height: 3,
            background: BRAND_RED,
            borderRadius: 2,
            marginBottom: 12,
          }}
        />
        <h3
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 24,
            fontWeight: 700,
            color: BRAND_NAVY,
            margin: "0 0 8px",
          }}
        >
          {program.degreeTitle}
        </h3>
        <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.6, margin: "0 0 1.5rem", maxWidth: 640 }}>
          {program.tagline}
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: "1.25rem",
            paddingTop: "1.25rem",
            borderTop: "1px solid #eee",
          }}
        >
          <StatPill label="Modality" value={program.modality} />
          <StatPill label="Length" value={program.length} />
          <StatPill label="Term" value={program.term} />
          <StatPill label="Total Credits" value={program.totalCredits} />
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: "1.25rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
        {Object.entries(TYPE_LABELS).map(([k, v]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <TypeTag type={k} />
            <span style={{ fontSize: 12.5, color: "#6b6b6b" }}>{v}</span>
          </div>
        ))}
      </div>

      {/* Semesters */}
      <div style={{ marginBottom: "2.5rem" }}>
        {program.semesters.map((s) => (
          <SemesterAccordion
            key={s.number}
            semester={s}
            isOpen={openSemester === s.number}
            onToggle={() => setOpenSemester(openSemester === s.number ? null : s.number)}
          />
        ))}
      </div>

      {/* Electives */}
      <div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: "1rem" }}>
          <h3
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 20,
              fontWeight: 700,
              color: BRAND_NAVY,
              margin: 0,
            }}
          >
            Elective Options
          </h3>
          <span style={{ fontSize: 12.5, color: "#999" }}>
            Choose from these to fill your Elective slots
          </span>
        </div>
        <ElectivesGrid electives={program.electives} />
      </div>
    </section>
  );
}